import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";

import {
  useHookForm,
  useMerchantApiMutation,
  useMerchantApiQuery,
} from "../../../hooks";
import {
  IBusinessType,
  IOnboardingStatus,
  IOnboardingStep2Request,
  IOnboardingStepResponse,
  IRevenueBracket,
} from "../../../interfaces-and-types";
import { globalHandlerUtils } from "../../../utils";
import { IOnboardingStepProps, IOnboardingStepViewProps } from "../Onboarding";

import { View } from "./View";

export interface IOnboardingStep2ViewProps
  extends IOnboardingStepViewProps<IOnboardingStep2Request> {
  isLoadingRevenueBrackets: boolean;
  revenueBrackets: IRevenueBracket[];
}

export const OnboardingStep2 = (props: IOnboardingStepProps) => {
  const {
    isLoading: isLoadingOnboardingStatus,
    isSuccess: isSuccessOnboardingStatus,
    data: onboardingStatus,
  } = useMerchantApiQuery<IOnboardingStatus>("getOnboardingStatus");

  const {
    isLoading: isLoadingStep2,
    isSuccess: isSuccessStep2,
    isError: isErrorStep2,
    data: apiResponse,
    mutate: saveOnboardingStep,
  } = useMerchantApiMutation<IOnboardingStep2Request, IOnboardingStepResponse>(
    "saveOnboardingStep2"
  ).usePostMutation;

  const {
    isLoading: isLoadingRevenueBrackets,
    isSuccess: isSuccessRevenueBrackets,
    data: revenueBrackets,
  } = useMerchantApiQuery<IRevenueBracket[]>("getRevenueBrackets");

  useEffect(() => {
    if (isSuccessStep2) {
      // display success message and request email confirmation
      props.handleNext();
    }
  }, [isSuccessStep2]);

  const initialFormValues: IOnboardingStep2Request = {
    registered_business_name: "",
    industry: "",
    business_email_address: null,
    business_website: "",
    business_description: "",
    estimated_monthly_sale_id: null,
    // business_terms_and_conditions_url: "",
    // business_privacy_url: "",

    business_address_line1: "",
    business_address_line2: null,
  };

  const onboardingStep2Schema: yup.SchemaOf<IOnboardingStep2Request> = yup
    .object({
      registered_business_name: yup
        .string()
        .nullable()
        .required("Registered Business Name is required"),
      industry: yup
        .string()
        .nullable()
        .required("Industry Of Operation is required"),
      business_email_address: yup
        .string()
        .nullable()
        .email("Invalid email")
        .required("Business email is required"),
      business_website: yup.string().nullable().optional(),
      // business_terms_and_conditions_url: yup.string().nullable().optional(),
      // business_privacy_url: yup.string().nullable().optional(),
      business_description: yup.string().nullable().optional(),
      estimated_monthly_sale_id: yup
        .string()
        .nullable()
        .required("Estimated Monthly Sale is required"),

      business_address_line1: yup
        .string()
        .nullable()
        .required("Business Address is required"),
      business_address_line2: yup.string().nullable().optional(),
    })
    .required();

  const handleApiSubmit = (data: IOnboardingStep2Request) => {
    saveOnboardingStep(data);
  };

  const handleError = (errors: any) => globalHandlerUtils.handleError(errors);

  const { onFormSubmit, errors, register, reset } = useHookForm({
    initialFormValues,
    validationSchema: onboardingStep2Schema,
    onValid: handleApiSubmit,
    onInvalid: handleError,
  });

  useEffect(() => {
    if (isSuccessOnboardingStatus) {
      reset(onboardingStatus); // set retrieved values on the form
    }
  }, [isSuccessOnboardingStatus]);

  return (
    <View
      {...props}
      handleSubmit={onFormSubmit}
      register={register}
      errors={errors}
      isLoadingRevenueBrackets={isLoadingRevenueBrackets}
      revenueBrackets={revenueBrackets || []}
    />
  );
};
